import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import { useNavigate } from "react-router-dom";

const TasksStatusChart = ({ data = {}, permits = {}, appliedFilters = {}, assets = [] }) => {
  const navigate = useNavigate();

  const canShowData =
    permits.isSuperuser || (permits.hasAnyRole && permits.canTasksRead);

  const fixedStatuses = ["Pendiente", "Iniciada", "Finalizada"];
  const totals = data?.Totales || {};

  const series = useMemo(
    () =>
      fixedStatuses.map((status) => ({
        name: status,
        data: [
          canShowData
            ? totals[status] || 0
            : Math.floor(Math.random() * 11),
        ],
      })),
    [totals, canShowData]
  );

  const colors = canShowData
    ? ["#E74C3C", "#3498DB", "#F39C12"]
    : ["#CCCCCC", "#AAAAAA", "#888888"];

  const maxValue = useMemo(() => {
    const allValues = series.flatMap((s) => s.data);
    return Math.max(...allValues, 0);
  }, [series]);

  // ✅ Función para construir URL con AMBOS filtros del Dashboard
  const buildNavigationUrl = (status) => {
    const params = new URLSearchParams();
    
    // Agregar parámetro del gráfico
    params.append('status', status);
    
    // ✅ Agregar filtros de ACTIVOS del Dashboard
    if (appliedFilters.asset && appliedFilters.asset.length > 0) {
      const assetNames = appliedFilters.asset
        .map(code => assets.find(a => a.code === code)?.name)
        .filter(Boolean);
      
      if (assetNames.length > 0) {
        params.append('assets', assetNames.join(','));
      }
    }
    
    // ✅ Agregar filtros de ÁREAS del Dashboard
    if (appliedFilters.area && appliedFilters.area.length > 0) {
      const allFilterAreas = assets.reduce((acc, asset) => {
        if (asset.filter_areas) {
          asset.filter_areas.forEach(area => {
            if (!acc.find(a => a.id === area.id)) {
              acc.push(area);
            }
          });
        }
        return acc;
      }, []);

      const areaNames = appliedFilters.area
        .map(id => allFilterAreas.find(a => a.id === id)?.name)
        .filter(Boolean);
      
      if (areaNames.length > 0) {
        params.append('areas', areaNames.join(','));
      }
    }
    
    return `/tasks?${params.toString()}`;
  };

  const options = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        events: {
          dataPointSelection: (event, chartContext, config) => {
            if (!canShowData) return;
            const status = fixedStatuses[config.seriesIndex];
            navigate(buildNavigationUrl(status)); // ✅ Navega con ambos filtros
          },
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "70%",
          borderRadius: 6,
        },
      },
      xaxis: { categories: ["Estado"], labels: { show: false } },
      yaxis: {
        min: 0,
        max: maxValue <= 1 ? 1 : Math.ceil(maxValue),
        tickAmount: maxValue <= 5 ? maxValue + 1 : 5,
        labels: {
          formatter: (val) => (Number.isInteger(val) ? val : ""),
        },
      },
      legend: { show: true, onItemClick: { toggleDataSeries: true } },
      colors,
      dataLabels: { enabled: true },
      tooltip: { y: { formatter: (val) => val ?? 0 } },
    }),
    [canShowData, colors, navigate, maxValue, appliedFilters, assets]
  );

  return (
    <div
      style={{
        maxWidth: "500px",
        margin: "0 auto",
        cursor: canShowData ? "default" : "not-allowed",
        position: "relative",
      }}
    >
      <Chart options={options} series={series} type="bar" height={375} />
      {!canShowData && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255,255,255,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            fontWeight: "bold",
            color: "#555",
          }}
        >
          No tienes permisos para ver este gráfico
        </div>
      )}
    </div>
  );
};

export default TasksStatusChart;